import { sx } from '../../../lib/sx'
import { PairedDeviceRow, PairingCard } from '../manager/GroupDashboard'
import type { Vals } from '../../../demo/vals'

/**
 * The member's own account screen: what they are on, whose group it is, and
 * what they have agreed to share.
 *
 * No identity block at the top — it is their own phone, so telling them their
 * name and email is filler. The group's name is the manager's, synced.
 */
export function Profile({ v }: { v: Vals }) {
  return (
    <div style={sx('position:absolute;inset:0;padding-top:64px;display:flex;flex-direction:column')}>
      <div style={sx('flex:1;min-height:0;overflow:auto;padding:0 20px 24px;display:flex;flex-direction:column;gap:12px')}>
        <button
          onClick={v.kTabConnect}
          style={sx(
            'width:36px;height:36px;flex:none;border-radius:var(--radius-full);border:none;' +
              'background:var(--bg-tertiary);cursor:pointer;display:flex;align-items:center;' +
              'justify-content:center;margin-bottom:10px'
          )}
        >
          <span
            style={sx(
              'width:18px;height:18px;background:var(--text-primary);' +
                '-webkit-mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat;' +
                'mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat'
            )}
          />
        </button>

        <div
          style={sx(
            'padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
              'border:1px solid var(--border-secondary);display:flex;flex-direction:column;gap:2px'
          )}
        >
          <span
            style={sx(
              'font-size:var(--fs-2xs);font-weight:var(--fw-medium);letter-spacing:.06em;' +
                'text-transform:uppercase;color:var(--text-secondary)'
            )}
          >
            Current subscription
          </span>
          <span style={sx('font-size:var(--fs-xl);font-weight:var(--fw-bold);letter-spacing:var(--ls-sm)')}>
            {v.memberPlanName}
          </span>
          <span style={sx('font-size:var(--fs-xs);color:var(--text-secondary)')}>
            {v.memberPlanLine}
          </span>
        </div>

        <SectionLabel>Your group</SectionLabel>

        <div
          style={sx(
            'padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
              'border:1px solid var(--border-secondary);display:flex;flex-direction:column;gap:10px'
          )}
        >
          <span style={sx('display:flex;align-items:center;gap:10px')}>
            <span
              style={sx(
                'width:20px;height:20px;flex:none;background:var(--text-primary);' +
                  '-webkit-mask:url(/assets/icons/account.svg) center/contain no-repeat;' +
                  'mask:url(/assets/icons/account.svg) center/contain no-repeat'
              )}
            />
            <span style={sx('font-size:var(--fs-md);font-weight:var(--fw-medium);flex:1;min-width:0')}>
              {v.groupName}
            </span>
          </span>
          <span style={sx('font-size:var(--fs-xs);line-height:18px;color:var(--text-secondary)')}>
            {v.memberGroupBody}
          </span>
        </div>

        {v.memberShowGroupFeatures ? <SectionLabel>Group features</SectionLabel> : null}

        {v.memberShowGroupFeatures && v.memberSupervisedBy ? (
          <div
            style={sx(
              'padding:16px 18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
                'border:1px solid var(--border-secondary);display:flex;flex-direction:column;gap:8px'
            )}
          >
            <span style={sx('display:flex;align-items:center;gap:10px')}>
              <span
                style={sx(
                  'width:20px;height:20px;flex:none;background:var(--text-primary);' +
                    '-webkit-mask:url(/assets/icons/security-score.svg) center/contain no-repeat;' +
                    'mask:url(/assets/icons/security-score.svg) center/contain no-repeat'
                )}
              />
              <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium);flex:1;min-width:0')}>
                {v.memberSupervisedBy.title}
              </span>
            </span>
            <span style={sx('font-size:var(--fs-xs);line-height:18px;color:var(--text-secondary)')}>
              {v.memberSupervisedBy.body}
            </span>
          </div>
        ) : null}

        {!v.memberShowGroupFeatures ? null : v.memberSharesFeatures ? (
          <div
            style={sx(
              'border-radius:var(--radius-lg);background:var(--bg-secondary);' +
                'border:1px solid var(--border-secondary);display:flex;flex-direction:column'
            )}
          >
            {v.memberShareRows.map((f, i) => (
              <button
                key={f.id}
                onClick={f.toggle}
                style={sx(
                  'padding:15px 18px;background:transparent;font-family:inherit;cursor:pointer;' +
                    'color:var(--text-primary);text-align:left;display:flex;align-items:center;gap:12px;' +
                    'border:none' +
                    (i ? ';border-top:1px solid var(--border-secondary)' : '')
                )}
              >
                <span style={sx('flex:1;min-width:0;font-size:var(--fs-sm);line-height:19px')}>
                  {f.name}
                </span>
                <span
                  style={sx(
                    `width:44px;height:26px;flex:none;border-radius:var(--radius-full);` +
                      `background:${f.trackBg};position:relative;transition:background 150ms var(--ease-out)`
                  )}
                >
                  <span
                    style={sx(
                      `position:absolute;top:3px;${f.knobSide};width:20px;height:20px;` +
                        `border-radius:var(--radius-full);background:#fff`
                    )}
                  />
                </span>
              </button>
            ))}
          </div>
        ) : (
          <>
            {/* Pairs this handset already holds come first, same as on the
                managing side: the handshake cards below are only how you add
                to them. */}
            {v.memberPairedDevices.map((d) => (
              <PairedDeviceRow key={d.key} v={v} d={d} />
            ))}
            <PairingCard card={v.shareCards[0]} onOpen={v.memberOpenShareInvite} />
            <PairingCard card={v.shareCards[1]} onOpen={v.memberOpenScanner} />
          </>
        )}
      </div>
    </div>
  )
}

function SectionLabel({ children }: { children: string }) {
  return (
    <span
      style={sx(
        'font-size:var(--fs-xs);font-weight:var(--fw-medium);color:var(--text-secondary);' +
          'letter-spacing:.04em;text-transform:uppercase;margin-top:6px'
      )}
    >
      {children}
    </span>
  )
}
